/**
 * Cookie utilities for secure token management
 * Following security best practices for refresh token storage
 */

/**
 * Set a secure cookie with best practices
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options
 */
export const setSecureCookie = (name, value, options = {}) => {
  const defaultOptions = {
    maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
    path: '/',
    secure: window.location.protocol === 'https:', // Only over HTTPS in production
    sameSite: 'Strict', // CSRF protection
    ...options
  };

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  
  Object.entries(defaultOptions).forEach(([key, val]) => {
    if (val === true) {
      cookieString += `; ${key}`;
    } else if (val !== false && val !== null && val !== undefined) {
      cookieString += `; ${key}=${val}`;
    }
  });

  document.cookie = cookieString;
};

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  if (parts.length === 2) {
    const cookieValue = parts.pop().split(';').shift();
    return decodeURIComponent(cookieValue);
  }
  
  return null;
};

/**
 * Delete a cookie by setting it to expire
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const deleteCookie = (name, path = '/') => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; secure; sameSite=Strict;`;
};

/**
 * Check if cookies are available/enabled
 * @returns {boolean} - True if cookies are available
 */
export const areCookiesAvailable = () => {
  try {
    const testCookie = '__cookie_test__';
    setSecureCookie(testCookie, 'test');
    const available = getCookie(testCookie) === 'test';
    deleteCookie(testCookie);
    return available;
  } catch (error) {
    console.warn('Cookies are not available:', error);
    return false;
  }
};

/**
 * Refresh token specific utilities
 */
export const REFRESH_TOKEN_COOKIE_NAME = 'refresh_token';

/**
 * Set refresh token in secure cookie
 * @param {string} token - Refresh token
 */
export const setRefreshToken = (token) => {
  setSecureCookie(REFRESH_TOKEN_COOKIE_NAME, token, {
    maxAge: 7 * 24 * 60 * 60, // 7 days
    secure: window.location.protocol === 'https:',
    sameSite: 'Strict'
  });
};

/**
 * Get refresh token from cookie
 * @returns {string|null} - Refresh token or null
 */
export const getRefreshToken = () => {
  return getCookie(REFRESH_TOKEN_COOKIE_NAME);
};

/**
 * Remove refresh token cookie
 */
export const removeRefreshToken = () => {
  deleteCookie(REFRESH_TOKEN_COOKIE_NAME);
}; 